'use client';

import { LogoFull, LogoOnwheel } from '@ahamove/icons';
import {
  Button,
  Card,
  Center,
  Space,
  Stack,
  Tabs,
  Text,
  rem,
  type StackProps,
} from '@mantine/core';
import { UserRound } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useAuth } from '../auth-provider';
import { AuthStep, AuthTab } from '../lib/constants';
import { useCustomRouter, usePathname } from '../lib/navigation';
import { authLocales, createTranslations, type Locales } from '../locales';
import { useAuthStore } from '../store/auth-store-provider';
import OtpForm from './otp-form';
import Passcode from './passcode';
import PasscodeForm from './passcode-form';
import ResetPasscodeForm from './reset-passcode-form';
import SignInForm from './sign-in-form';
import SignUpForm from './sign-up-form';

export type AuthFormProps = Omit<StackProps, 'children'> & {
  asPage?: boolean;
  lang?: keyof Locales;
};

export default function AuthForm({
  lang = 'vi',
  asPage = false,
  ...props
}: AuthFormProps) {
  const tAuth = createTranslations({ locales: authLocales, lang });

  const pathname = usePathname();
  const { navigate } = useCustomRouter();
  const { page } = useAuth();

  const { activeAuthStep, setActiveAuthStep, authTabScreen, setAuthTabScreen } =
    useAuthStore((state) => state);

  useEffect(() => {
    return () => {
      const currentParams = new URLSearchParams(window.location.search);
      if (!currentParams.has('sessionId')) {
        setActiveAuthStep(AuthStep.PhoneNumber);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOnChangeAuthTabScreen = (
    value: string | null = AuthTab.SignIn
  ) => {
    if (asPage) {
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.has('sessionId')) {
        currentParams.delete('sessionId');
      }

      const nextUrl = `${value}?${currentParams.toString()}`;
      navigate(nextUrl);
    } else {
      setAuthTabScreen(value as AuthTab);
    }
  };

  const shouldShowSignInBanner = useMemo(() => {
    return (
      ((asPage && pathname === AuthTab.SignIn.toString()) ||
        (!asPage && authTabScreen === AuthTab.SignIn)) &&
      activeAuthStep === AuthStep.PhoneNumber
    );
  }, [asPage, authTabScreen, pathname, activeAuthStep]);

  return (
    <>
      <Card.Section
        hiddenFrom="sm"
        mb={rem(shouldShowSignInBanner ? rem(-16) : rem(32))}
        pos="relative"
        flex="none"
        styles={{
          section: {
            transitionProperty: 'all',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDuration: '150ms',
            aspectRatio: shouldShowSignInBanner ? '3/2' : '0',
            backgroundImage:
              'url(https://cdn-ahamoveimg.ahamove.com/web/auth/sign-in-vector.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          },
        }}
      ></Card.Section>
      <Stack
        p={{ base: 'md', sm: '0' }}
        w="100%"
        gap={40}
        h="100%"
        flex={1}
        bg="white"
        styles={{
          root: {
            zIndex: 10,
            borderTopLeftRadius: rem(12),
            borderTopRightRadius: rem(12),
          },
        }}
        {...props}
      >
        <Center visibleFrom="sm">
          {page === 'onwheel' ? (
            <LogoOnwheel height={32} width={185} />
          ) : (
            <LogoFull height={32} width={185} />
          )}
        </Center>

        <Tabs
          flex={1}
          value={asPage ? pathname : authTabScreen}
          onChange={handleOnChangeAuthTabScreen}
          display="flex"
          styles={{
            root: {
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: rem(24),
            },
            tabLabel: {
              fontWeight: 700,
            },
            tab: {
              paddingTop: rem(8),
              paddingBottom: rem(8),
            },
            panel: {
              flex: 1,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            },
          }}
        >
          {pathname !== AuthTab.NewPasscode.toString() &&
          (activeAuthStep === AuthStep.PhoneNumber ||
            pathname === AuthTab.SignUp.toString()) ? (
            <Tabs.List>
              <Tabs.Tab value={AuthTab.SignIn}>{tAuth('signIn')}</Tabs.Tab>
              <Tabs.Tab value={AuthTab.SignUp}>{tAuth('signUp')}</Tabs.Tab>
            </Tabs.List>
          ) : null}

          <Tabs.Panel value={AuthTab.SignIn}>
            <Tabs
              value={activeAuthStep}
              w="100%"
              display="flex"
              flex={1}
              styles={{
                root: {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: rem(24),
                },
                panel: {
                  flex: 1,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                },
              }}
            >
              <Tabs.Panel value={AuthStep.Home}>
                <Stack w="100%" gap="md" flex={1}>
                  <Button
                    px="md"
                    h={rem(44)}
                    fullWidth
                    variant="default"
                    onClick={() => {
                      setActiveAuthStep(AuthStep.PhoneNumber);
                    }}
                    leftSection={<UserRound size={24} />}
                    rightSection={<Space w={24} />}
                    justify="space-between"
                  >
                    {tAuth('phoneNumber')}
                  </Button>

                  <Text size="xs" c="neutral.6" fw={500}>
                    {tAuth('privacyPolicyAndTermsOfService')}
                    <Text
                      component="a"
                      c="neutral.6"
                      fw={600}
                      href="https://www.ahamove.com/policy"
                      target="_blank"
                    >
                      {tAuth('privacyPolicyAndTermsOfServiceLink')}
                    </Text>
                  </Text>
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value={AuthStep.PhoneNumber}>
                <SignInForm />
              </Tabs.Panel>
              <Tabs.Panel value={AuthStep.OTP}>
                <OtpForm />
              </Tabs.Panel>
              <Tabs.Panel value={AuthStep.PassCode}>
                <PasscodeForm />
              </Tabs.Panel>
              <Tabs.Panel value={AuthStep.CreatePasscode}>
                <Passcode />
              </Tabs.Panel>
            </Tabs>
          </Tabs.Panel>
          <Tabs.Panel value={AuthTab.SignUp}>
            <SignUpForm asPage={asPage} />
          </Tabs.Panel>
          <Tabs.Panel value={AuthTab.NewPasscode}>
            <ResetPasscodeForm />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </>
  );
}
